import React from 'react';
import { ChevronDown, ChevronRight, UserCog } from 'lucide-react';
import { HierarchyNode } from '@/services/PagesServices/OrgHierarchyService';

interface OrgChartNodeProps {
  node: HierarchyNode;
  highlightUuid: string | null;
  collapsed: Set<string>;
  onToggle: (uuid: string) => void;
  onReassign: (node: HierarchyNode) => void;
}

/**
 * Recursive org-chart node using the classic CSS connector technique
 * (nested <ul>/<li> with pseudo-element borders — see OrgHierarchy.css).
 */
const OrgChartNode: React.FC<OrgChartNodeProps> = ({ node, highlightUuid, collapsed, onToggle, onReassign }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isCollapsed = collapsed.has(node.uuid);
  const isHighlighted = highlightUuid === node.uuid;

  return (
    <li className="s-admin-org-li">
      <div
        id={`org-node-${node.uuid}`}
        className={`s-admin-org-node ${isHighlighted ? 's-admin-org-node-hl' : ''}`}
      >
        <div className="s-admin-org-node-main">
          <div className="s-admin-org-avatar">{(node.name || '?').charAt(0).toUpperCase()}</div>
          <div className="s-admin-org-node-info">
            <span className="s-admin-org-node-name" title={node.name}>{node.name}</span>
            <span className="s-admin-org-node-role">{node.roleLabel || '—'}</span>
          </div>
        </div>
        <div className="s-admin-org-node-foot">
          <button
            className="s-admin-org-node-reassign"
            onClick={() => onReassign(node)}
            title="Reassign manager"
          >
            <UserCog size={13} /> Manager
          </button>
          {hasChildren && (
            <button className="s-admin-org-node-toggle" onClick={() => onToggle(node.uuid)}>
              {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              {node.directReports}
            </button>
          )}
        </div>
      </div>

      {hasChildren && !isCollapsed && (
        <ul className="s-admin-org-children">
          {node.children.map((child) => (
            <OrgChartNode
              key={child.uuid}
              node={child}
              highlightUuid={highlightUuid}
              collapsed={collapsed}
              onToggle={onToggle}
              onReassign={onReassign}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default OrgChartNode;
